import { http } from '../../services/http'

export type FileTagType = 'private' | 'public'
export type PrivateFileTag = { title?: string; url?: string }

const fileTagsCache: Partial<Record<FileTagType, PrivateFileTag[]>> = {}

export function clearPrivateFileTagsCache(): void {
  delete fileTagsCache.private
}

export function clearFileTagsCache(type?: FileTagType): void {
  if (type) {
    delete fileTagsCache[type]
    return
  }
  delete fileTagsCache.private
  delete fileTagsCache.public
}

export function fileTagTypeForResourcePrefix(resourcePrefix: 'private' | 'public' | 'global_public'): FileTagType {
  return resourcePrefix === 'private' ? 'private' : 'public'
}

export function parsePrivateFileTagsResponse(
  data: { results?: PrivateFileTag[] } | PrivateFileTag[] | undefined | null
): PrivateFileTag[] {
  if (!data) {
    return []
  }
  if (Array.isArray(data)) {
    return data
  }
  return data.results ?? []
}

export async function fetchFileTags(
  type: FileTagType,
  get: (options: { url: string }) => Promise<{ data?: { results?: PrivateFileTag[] } | PrivateFileTag[] }> = http.os
    .get
): Promise<PrivateFileTag[]> {
  const cached = fileTagsCache[type]
  if (cached) {
    return cached
  }
  try {
    const res = await get({
      url: `file_tags/?type=${type}&per_page=200`,
    })
    const tags = parsePrivateFileTagsResponse(res.data)
    fileTagsCache[type] = tags
    return tags
  } catch (error) {
    console.error(`Failed to fetch ${type} file tags:`, error)
    return []
  }
}

export async function fetchPrivateFileTags(
  get: (options: { url: string }) => Promise<{ data?: { results?: PrivateFileTag[] } | PrivateFileTag[] }> = http.os
    .get
): Promise<PrivateFileTag[]> {
  return fetchFileTags('private', get)
}

export async function fetchPublicFileTags(
  get: (options: { url: string }) => Promise<{ data?: { results?: PrivateFileTag[] } | PrivateFileTag[] }> = http.os
    .get
): Promise<PrivateFileTag[]> {
  return fetchFileTags('public', get)
}

export function mapTagTitlesToUrls(titles: string[], tags: PrivateFileTag[]): string[] {
  return titles
    .map((title) => tags.find((tag) => tag.title === title)?.url)
    .filter((url): url is string => Boolean(url))
}

export async function resolveTagUrlsForSlot(
  tagUrls: string[],
  titles: string[],
  fetchTags: () => Promise<PrivateFileTag[]> = fetchPrivateFileTags
): Promise<string[]> {
  if (tagUrls.length) {
    return tagUrls
  }
  if (!titles.length) {
    return []
  }
  const tags = await fetchTags()
  return mapTagTitlesToUrls(titles, tags)
}
